import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
    getPingLogDetail,
    PingLogDetailData,
} from './pingLogService';
import './PingLog.css';

const PingLogDetailPage: React.FC = () => {
    const { logId = '' } = useParams();
    const navigate = useNavigate();

    const [detail, setDetail] = useState<PingLogDetailData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isChartDragging, setIsChartDragging] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            const detailData = await getPingLogDetail(logId);
            setDetail(detailData);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load ping log detail.');
            setDetail(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (logId) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logId]);

    useEffect(() => {
        const handleWindowMouseUp = () => {
            setIsChartDragging(false);
        };

        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, []);

    const displayStartClockLine = useMemo(() => {
        if (!detail?.records?.length) return '-';
        return detail.records[0]?.rawClockLine || '-';
    }, [detail]);

    const displayEndClockLine = useMemo(() => {
        if (!detail?.records?.length) return '-';
        return detail.records[detail.records.length - 1]?.rawClockLine || '-';
    }, [detail]);

    const formatClockLineShortForAxis = (raw?: string) => {
        if (!raw) return '-';

        const s = raw.trim();
        const dateMatch = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        const timeMatch = s.match(/(\d{1,2}):(\d{2}):(\d{2})/);

        if (!timeMatch) {
            return s;
        }

        const [, hour, minute, second] = timeMatch;

        if (!dateMatch) {
            return `${hour.padStart(2, '0')}:${minute}:${second}`;
        }

        const [, , month, day] = dateMatch;
        return `${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour.padStart(2, '0')}:${minute}`;
    };

    const chartData = useMemo(() => {
        const records = detail?.records || [];

        return records.map((record, index) => {
            const rawLabel = record.rawClockLine || `Round ${index + 1}`;

            return {
                index: index + 1,
                clockLine: rawLabel,
                chartAxisShortLabel: formatClockLineShortForAxis(rawLabel),
                lossPercent: Number(record.lossPercent ?? 0),
                rttAvgMs: Number(record.rttAvgMs ?? 0),
                sourceDeviceName: record.deviceName || detail?.task?.deviceName || '-',
                sourceTargetIp: record.targetIp || detail?.task?.targetIp || '-',
                sourceSourceIp: record.sourceIp || detail?.task?.sourceIp || '-',
                vrf: record.vrf || '-',
            };
        });
    }, [detail]);

    const zoomStart = 0;
    const zoomEnd = 100;

    const peakLabelIndexSet = useMemo(() => {
        if (!chartData.length) {
            return new Set<number>();
        }

        const maxPlrIndex = chartData.reduce((maxIdx, item, index, arr) => {
            return item.lossPercent > arr[maxIdx].lossPercent ? index : maxIdx;
        }, 0);

        const maxRttIndex = chartData.reduce((maxIdx, item, index, arr) => {
            return item.rttAvgMs > arr[maxIdx].rttAvgMs ? index : maxIdx;
        }, 0);

        return new Set<number>([maxPlrIndex, maxRttIndex]);
    }, [chartData]);

    const chartOption = useMemo(() => {
        return {
            backgroundColor: 'transparent',
            animation: false,
            tooltip: {
                trigger: 'axis',
                confine: true,
                backgroundColor: '#ffffff',
                borderColor: '#dee2e6',
                borderWidth: 1,
                textStyle: {
                    color: '#212529',
                },
                formatter: (params: any) => {
                    if (!params || !params.length) return '';

                    const point = params[0]?.data;
                    if (!point) return '';

                    return `
                        <div style="min-width:300px;">
                            <div style="margin-bottom:6px;color:#6c757d;word-break:break-word;">
                                Clock: ${point.clockLine || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Device: ${point.sourceDeviceName || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Source IP: ${point.sourceSourceIp || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Target IP: ${point.sourceTargetIp || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                VRF: ${point.vrf || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#dc3545;">
                                PLR: ${point.lossPercent ?? '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#0d6efd;">
                                RTD Avg: ${point.rttAvgMs ?? '-'}
                            </div>
                        </div>
                    `;
                },
            },
            legend: {
                top: 8,
                textStyle: {
                    color: '#ffffff',
                },
            },
            grid: {
                top: 60,
                left: 50,
                right: 60,
                bottom: 135,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chartData.map((item) => item.chartAxisShortLabel),
                axisLabel: {
                    color: '#ffffff',
                    rotate: 45,
                    hideOverlap: true,
                    margin: 16,
                    fontSize: 11,
                    formatter: (value: string, index: number) => {
                        const lastIndex = chartData.length - 1;

                        if (index === 0 || index === lastIndex) {
                            return value;
                        }

                        if (peakLabelIndexSet.has(index)) {
                            return value;
                        }

                        const step =
                            chartData.length > 240 ? 20 :
                            chartData.length > 120 ? 10 :
                            chartData.length > 60 ? 5 : 2;

                        return index % step === 0 ? value : '';
                    },
                },
                axisLine: {
                    lineStyle: {
                        color: 'rgba(255,255,255,0.25)',
                    },
                },
                axisTick: {
                    lineStyle: {
                        color: 'rgba(255,255,255,0.25)',
                    },
                },
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'PLR',
                    position: 'left',
                    axisLabel: {
                        color: '#ffffff',
                    },
                    nameTextStyle: {
                        color: '#ffffff',
                    },
                    splitLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                            type: 'dashed',
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                        },
                    },
                },
                {
                    type: 'value',
                    name: 'RTD Avg',
                    position: 'right',
                    axisLabel: {
                        color: '#ffffff',
                    },
                    nameTextStyle: {
                        color: '#ffffff',
                    },
                    splitLine: {
                        show: false,
                    },
                    axisLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                        },
                    },
                },
            ],
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    start: zoomStart,
                    end: zoomEnd,
                    zoomOnMouseWheel: false,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: true,
                    preventDefaultMouseMove: true,
                },
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    height: 24,
                    bottom: 30,
                    start: zoomStart,
                    end: zoomEnd,
                    borderColor: 'rgba(255,255,255,0.20)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    fillerColor: 'rgba(13,110,253,0.28)',
                    dataBackground: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.35)',
                            width: 1,
                        },
                        areaStyle: {
                            color: 'rgba(255,255,255,0.10)',
                        },
                    },
                    selectedDataBackground: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.55)',
                            width: 1,
                        },
                        areaStyle: {
                            color: 'rgba(255,255,255,0.18)',
                        },
                    },
                    moveHandleSize: 10,
                    moveHandleStyle: {
                        color: 'rgba(13,110,253,0.85)',
                        borderColor: '#ffffff',
                        borderWidth: 1,
                        shadowBlur: 4,
                        shadowColor: 'rgba(0,0,0,0.25)',
                    },
                    handleSize: '95%',
                    handleStyle: {
                        color: 'rgba(13,110,253,0.95)',
                        borderColor: '#ffffff',
                        borderWidth: 1.2,
                        shadowBlur: 6,
                        shadowColor: 'rgba(0,0,0,0.28)',
                    },
                    textStyle: {
                        color: '#ffffff',
                    },
                    brushSelect: false,
                },
            ],
            series: [
                {
                    name: 'PLR',
                    type: 'line',
                    yAxisIndex: 0,
                    showSymbol: false,
                    smooth: false,
                    data: chartData.map((item) => ({
                        value: item.lossPercent,
                        ...item,
                    })),
                    lineStyle: {
                        width: 2,
                        color: '#dc3545',
                    },
                    itemStyle: {
                        color: '#dc3545',
                    },
                },
                {
                    name: 'RTD Avg',
                    type: 'line',
                    yAxisIndex: 1,
                    showSymbol: false,
                    smooth: false,
                    data: chartData.map((item) => ({
                        value: item.rttAvgMs,
                        ...item,
                    })),
                    lineStyle: {
                        width: 2,
                        color: '#0d6efd',
                    },
                    itemStyle: {
                        color: '#0d6efd',
                    },
                },
            ],
        };
    }, [chartData, peakLabelIndexSet]);

    const handleChartMouseDown = () => {
        setIsChartDragging(true);
    };

    const handleChartMouseUp = () => {
        setIsChartDragging(false);
    };

    const handleChartMouseLeave = () => {
        setIsChartDragging(false);
    };

    if (loading) {
        return (
            <div className="mt-3 text-center">
                <Spinner animation="border" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-3 pinglog-page">
                <Alert variant="danger">{error}</Alert>
                <Button variant="secondary" onClick={() => navigate('/pinglog')}>
                    Back to List
                </Button>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className="mt-3 pinglog-page">
                <Alert variant="warning">No data found.</Alert>
                <Button variant="secondary" onClick={() => navigate('/pinglog')}>
                    Back to List
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-3 pinglog-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">Ping Log Detail</h4>
                    <div className="text-muted">Log ID: {detail.task.logId}</div>
                </div>
                <div>
                    <Button variant="secondary" onClick={() => navigate('/pinglog')}>
                        Back to List
                    </Button>
                </div>
            </div>

            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-3">Basic Information</h5>

                    <Row className="mb-2">
                        <Col md={4}><strong>Log ID:</strong> {detail.task.logId}</Col>
                        <Col md={4}><strong>File Name:</strong> {detail.task.fileName}</Col>
                        <Col md={4}><strong>Device:</strong> {detail.task.deviceName || '-'}</Col>
                    </Row>

                    <Row className="mb-2">
                        <Col md={4}><strong>Agent:</strong> {detail.task.agentId || '-'}</Col>
                        <Col md={4}><strong>Log Type:</strong> {detail.task.logType || '-'}</Col>
                        <Col md={4}><strong>Total Rounds:</strong> {detail.task.totalRounds}</Col>
                    </Row>

                    <Row className="mb-2">
                        <Col md={6}><strong>Source IP:</strong> {detail.task.sourceIp || '-'}</Col>
                        <Col md={6}><strong>Target IP:</strong> {detail.task.targetIp || '-'}</Col>
                    </Row>

                    <Row className="mb-2">
                        <Col md={6}><strong>Start Time:</strong> {displayStartClockLine}</Col>
                        <Col md={6}><strong>End Time:</strong> {displayEndClockLine}</Col>
                    </Row>

                    {detail.task.errorMessage && (
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mb-0 mt-2">
                                    {detail.task.errorMessage}
                                </Alert>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div>
                            <h5 className="mb-1">Ping Trend</h5>
                            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                PLR and RTD Avg by round
                            </div>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                            Drag chart or bottom slider horizontally
                        </div>
                    </div>

                    {chartData.length === 0 ? (
                        <div className="text-center text-muted py-5">No round data</div>
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: 560,
                                cursor: isChartDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                            }}
                            onMouseDown={handleChartMouseDown}
                            onMouseUp={handleChartMouseUp}
                            onMouseLeave={handleChartMouseLeave}
                        >
                            <ReactECharts
                                option={chartOption}
                                style={{
                                    height: '560px',
                                    width: '100%',
                                    cursor: isChartDragging ? 'grabbing' : 'grab',
                                }}
                                notMerge={true}
                                lazyUpdate={true}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default PingLogDetailPage;